import cn from 'clsx'
import { FC, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'

interface SnackbarProps {
	message: string
	isVisible: boolean
	onClose: () => void
	duration?: number
	container?: HTMLElement | null
}

export const Snackbar: FC<SnackbarProps> = ({
	message,
	isVisible,
	onClose,
	duration = 5000,
	container
}) => {
	const [shouldRender, setShouldRender] = useState(false)
	const [isAnimating, setIsAnimating] = useState(false)

	useEffect(() => {
		if (isVisible) {
			setShouldRender(true)
			requestAnimationFrame(() => {
				setIsAnimating(true)
			})
		} else {
			setIsAnimating(false)
			const timer = setTimeout(() => {
				setShouldRender(false)
			}, 300)
			return () => clearTimeout(timer)
		}
	}, [isVisible])

	useEffect(() => {
		if (isVisible && duration > 0) {
			const timer = setTimeout(() => {
				onClose()
			}, duration)
			return () => clearTimeout(timer)
		}
	}, [isVisible, duration, onClose])

	if (!shouldRender) return null

	const snackbarContent = (
		<div
			className={cn(
				container ? 'absolute' : 'fixed',
				'bottom-6 left-1/2 -translate-x-1/2 z-[100]',
				'transition-all duration-300 ease-in-out',
				isAnimating
					? 'opacity-100 translate-y-0'
					: 'opacity-0 translate-y-4 pointer-events-none'
			)}
		>
			<div className='bg-gray-100 text-white font-[14px] px-3 py-2 rounded-lg shadow-lg flex items-center gap-3 min-w-fit max-w-[90vw]'>
				<div className='flex-1 text-sm text-black'>{message}</div>
			</div>
		</div>
	)

	if (container) {
		return createPortal(snackbarContent, container)
	}

	return snackbarContent
}
